import { AdAlertListCard } from '@backoffice-frontend/ad-alert-cards'
import { CabinLivestream } from '@backoffice-frontend/ad-operator-video'
import {
  AdOnBoardPassengers,
  AdWaypointPassengers,
  PassengerAuthenticationsProvider,
} from '@backoffice-frontend/ad-passenger-list'
import {
  AdSeatStatus,
  AdTelematicsCard,
  BookabilityCard,
  SelfDrivingSystemCard,
  SystemComponentsCard,
} from '@backoffice-frontend/ad-telematics-cards'
import {
  useErrorHandlingMutation,
  useErrorHandlingQuery,
} from '@backoffice-frontend/data-fetching'
import {
  FccAdAlertStatus,
  FccAdAlertType,
  FccWaypointState,
} from '@backoffice-frontend/graphql'
import { adVehicleFetchIntervalInMs } from '@backoffice-frontend/vehicle-common'
import { VehicleMissionCard } from '@backoffice-frontend/vehicle-mission'
import { RouteStopIcon, WarningIcon } from '@moia-dev/pace-icons'
import {
  Badge,
  Button,
  Card,
  Dialog,
  LoadingIndicator,
  useDialog,
} from '@moia-dev/pace-web'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AdVehicleDoorStatus } from '../AdVehicleDoorStatus'
import { AdVehicleDoorStatusProvider } from '../AdVehicleDoorStatus/AdDoorStatusProvider'
import { useAdVehicleStream } from '../AdVehicleStream/useAdVehicleStream'
import { AlertVehicleMap } from '../AlertVehicleMap'
import { evaluateCabinSafetyCheckStatus } from '../evaluateCabinSafetyCheckStatus'
import { useServiceCancellation } from '../ServiceCancellation/useServiceCancellation'
import { ActiveActionCard } from './ActiveActionCard'
import {
  useAdVehicleStreamQuery,
  useAdVehicleStreamStopHandlingVehicleMutation,
} from './AdVehicleStream.hook'
import { AdVehicleStreamHeader } from './AdVehicleStreamHeader/AdVehicleStreamHeader'
import { useIsOverflow } from './useIsOverflow'

const CALL_ALERT_TYPES: FccAdAlertType[] = [
  FccAdAlertType.PassengerCalling,
  FccAdAlertType.OperatorCalling,
  FccAdAlertType.SafetyDriverCalling,
  FccAdAlertType.IncomingPassengerCall,
  FccAdAlertType.OutgoingOperatorCall,
  FccAdAlertType.OutgoingOperatorToSafetyDriverCall,
  FccAdAlertType.IncomingSafetyDriverCall,
]

export const AdVehicleStream = () => {
  const { t } = useTranslation()
  const { vehicleId, setPendingUpdate, isHandledByCurrentUser } =
    useAdVehicleStream()
  const closeConfirmDialog = useDialog(undefined, 1400)

  const scrollableColumnRef = useRef<HTMLDivElement>(null)
  const isScrollableColumnOverflowing = useIsOverflow(scrollableColumnRef)

  const { data: vehicleData, loading } = useErrorHandlingQuery(
    useAdVehicleStreamQuery,
    vehicleId && isHandledByCurrentUser
      ? {
          initialFetchPolicy: 'no-cache',
          pollInterval: adVehicleFetchIntervalInMs,
          variables: {
            vehicleId,
          },
        }
      : { skip: true },
  )
  const [stopHandlingVehicle] = useErrorHandlingMutation(
    useAdVehicleStreamStopHandlingVehicleMutation,
  )
  const { abandon: abandonServiceCancellation } = useServiceCancellation()

  const vehicle = vehicleData?.vehicleByID
  const highestPriorityAlert = vehicle?.fccOperatorAdAlerts?.alerts.at(0)

  if (
    loading || // avoid returning stale data from previous request
    !isHandledByCurrentUser ||
    !vehicleId ||
    !vehicle ||
    !highestPriorityAlert
  ) {
    return (
      <Card
        outline={true}
        css={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: 'var(--shadow-depth-2)',
        }}
      >
        <LoadingIndicator size={32} />
      </Card>
    )
  }

  const waypoint = vehicle.fccUpcomingWaypoints?.at(0)
  const futureWaypoints = vehicle.fccUpcomingWaypoints?.slice(1) ?? []

  const { isStandaloneCabinSafetyCheck } =
    evaluateCabinSafetyCheckStatus(waypoint)

  const hasCallAlert = vehicle.fccOperatorAdAlerts?.alerts.some(
    alert =>
      CALL_ALERT_TYPES.includes(alert.type) &&
      alert.status === FccAdAlertStatus.Open,
  )

  const closeModal = () => {
    setPendingUpdate(true)
    abandonServiceCancellation()
    stopHandlingVehicle({ variables: { vehicleId } })
  }

  const handleClose = () => {
    if (hasCallAlert) {
      closeConfirmDialog.openDialog()
    } else {
      closeModal()
    }
  }
  const isAtWaypoint =
    waypoint?.adState === FccWaypointState.Reached ||
    waypoint?.adState === FccWaypointState.Completed

  return (
    <PassengerAuthenticationsProvider>
      <AdVehicleDoorStatusProvider vehicle={vehicle}>
        <Card
          outline={true}
          css={{
            width: '100%',
            padding: 0,
            overflow: 'hidden',
            boxShadow: 'var(--shadow-depth-2)',
          }}
        >
          <div
            css={theme => ({
              height: '100%',
              minHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: theme.semantic.ColorBackgroundDefault,
            })}
          >
            <AdVehicleStreamHeader vehicle={vehicle} onClose={handleClose} />
            <div
              // modal content container
              css={{
                flex: 1,
                overflow: 'hidden',
              }}
            >
              <div
                css={theme => ({
                  display: 'flex',
                  gap: theme.space.Space2,
                  height: '100%',
                })}
              >
                <div
                  // left side of the modal content (independently scrollable)
                  css={theme => ({
                    flex: '1',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.space.Space2,
                    padding: `${theme.space.Space2} 0 ${theme.space.Space2} ${theme.space.Space2}`,
                    overflowY: 'auto',
                    '@media (max-width: 1500px)': {
                      minWidth: '450px',
                    },
                  })}
                >
                  <CabinLivestream vehicleId={vehicle.id} />
                  <div
                    css={theme => ({
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'stretch',
                      '@media (max-width: 1500px)': {
                        flexDirection: 'column',
                      },
                      gap: theme.space.Space2,
                    })}
                  >
                    <div
                      css={{
                        flex: 6,
                        minHeight: '225px',
                      }}
                    >
                      <AlertVehicleMap
                        vehicle={vehicle}
                        highlightedWaypoint={waypoint}
                      />
                    </div>

                    {/*Vehicle Seat Card and Door Status*/}
                    <Card
                      outline
                      css={theme => ({
                        alignSelf: 'flex-start',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.space.Space2,
                        alignItems: 'center',
                      })}
                    >
                      <div
                        css={{
                          display: 'flex',
                          margin: '0 auto',
                        }}
                      >
                        {isAtWaypoint ? (
                          <Badge
                            icon={<RouteStopIcon />}
                            variant={'success'}
                            label={t('adFleetMap_badge_waypoint_label')}
                          />
                        ) : (
                          <Badge
                            icon={<WarningIcon />}
                            label={t('adFleetMap_badge_noWaypoint_label')}
                          />
                        )}
                      </div>

                      <AdSeatStatus vehicleId={vehicle.id} />
                      <AdVehicleDoorStatus />
                    </Card>
                  </div>
                </div>
                <div
                  // right side of the modal content
                  css={theme => ({
                    flex: '0 1 clamp(680px, 50%, 1500px)',
                    display: 'flex',
                    gap: theme.space.Space2,
                  })}
                >
                  <div
                    css={{
                      position: 'relative',
                      minWidth: '456px',
                      '@media (max-width: 1500px)': {
                        minWidth: '350px',
                      },
                    }}
                  >
                    <div
                      // scrollable column
                      css={theme => ({
                        position: 'absolute',
                        inset: 0,
                        overflowY: 'auto',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        flexDirection: 'column',
                        gap: theme.space.Space2,
                        padding: `${theme.space.Space2} 0`,
                        ...(isScrollableColumnOverflowing && {
                          paddingRight: theme.space.Space2,
                        }),
                      })}
                      ref={scrollableColumnRef}
                    >
                      {waypoint &&
                      vehicle.fccUpcomingWaypoints &&
                      !isStandaloneCabinSafetyCheck ? (
                        <Card outline css={{ padding: 0 }}>
                          <AdWaypointPassengers
                            waypoint={waypoint}
                            futureWaypoints={futureWaypoints}
                          />
                        </Card>
                      ) : (
                        <AdOnBoardPassengers vehicle={vehicle} />
                      )}

                      <VehicleMissionCard
                        vehicle={vehicle}
                        css={{ marginBottom: 0 }}
                      />

                      <BookabilityCard vehicle={vehicle} />

                      <SelfDrivingSystemCard vehicleId={vehicle.id} />

                      <SystemComponentsCard vehicle={vehicle} />

                      <AdTelematicsCard vehicleId={vehicle.id} />
                    </div>
                  </div>
                  <div
                    css={theme => ({
                      flexGrow: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: theme.space.Space2,
                      minWidth: '600px',
                      overflowY: 'auto',
                      padding: theme.space.Space2,
                      paddingLeft: 0,
                      '@media (max-width: 2000px)': {
                        minWidth: '450px',
                      },
                    })}
                  >
                    <AdAlertListCard
                      vehicle={vehicle}
                      disableAction
                      disableWaypointPassengers
                    />
                    <div css={{ flexGrow: 1 }}>
                      <ActiveActionCard
                        vehicle={vehicle}
                        onClose={handleClose}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
        {closeConfirmDialog.isOpen && (
          <Dialog
            id={closeConfirmDialog.id}
            title={t('adFleetMap_dialog_closeWithCallAlert_header')}
            onClose={closeConfirmDialog.closeDialog}
            actions={
              <>
                <Button
                  onClick={closeConfirmDialog.closeDialog}
                  label={t('common_cancel_button')}
                  variant="secondary"
                  size="compact"
                />
                <Button
                  onClick={() => {
                    closeConfirmDialog.closeDialog()
                    closeModal()
                  }}
                  label={t('common_close_button')}
                  size="compact"
                  variant="critical"
                />
              </>
            }
          >
            <p>{t('adFleetMap_dialog_closeWithCallAlert_caption')}</p>
          </Dialog>
        )}
      </AdVehicleDoorStatusProvider>
    </PassengerAuthenticationsProvider>
  )
}
